import { DotIndicator } from "./dot-indicator";
import { GALLERY_CHANGE_EVENT } from "./events";
import { type ChangeEvent } from "./gallery";
import { InteractionObserver } from "./utils";

const template = document.createElement("template");

template.innerHTML = `
  <style>
    :host {
      display: block;
      position: relative;
    }

    :host([hidden]) {
      display: none;
    }

    .scroll-container {
      display: flex;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scrollbar-width: none;
    }

    .scroll-container::-webkit-scrollbar {
      display: none;
    }

    .items::slotted(*) {
      flex: none;
      inline-size: 100%;
      scroll-snap-align: start;
    }

    .navigation {
      aspect-ratio: var(--ws-gallery-embed-navigation-aspect-ratio);
      display: var(--ws-gallery-embed-navigation-display);
      inset-block-start: 0;
      inset-inline: 24px;
      pointer-events: none;
      position: absolute;
      z-index: var(--ws-gallery-embed-navigation-z-index);
    }

    .navigation__buttons {
      display: flex;
      gap: var(--ws-gallery-embed-navigation-buttons-gap);
      inset-block: var(--ws-gallery-embed-navigation-buttons-inset-block);
      inset-inline: var(--ws-gallery-embed-navigation-buttons-inset-inline);
      justify-content: space-between;
      position: absolute;
      transform: var(--ws-gallery-embed-navigation-buttons-transform);
    }
  </style>
  <div class="scroll-container">
    <slot class="items"></slot>
  </div>
  <nav class="navigation">
    <slot name="counter"></slot>
    <div class="navigation__buttons">
      <slot name="previous"></slot>
      <slot name="next"></slot>
    </div>
  </nav>
  <div class="dots">
    <slot name="dots"></slot>
  </div>
</div>
`;

export class GalleryEmbed extends HTMLElement {
  #currentItem: HTMLElement;
  #currentItemNodeIndex = 0;
  #dots: DotIndicator;
  #interactionObserver?: InteractionObserver;
  #intersectionCounter = 0;
  #itemCounter: HTMLElement;
  #itemIndex: string | undefined;
  #items: HTMLElement[];
  #itemsObserver: IntersectionObserver;
  #mutationObserver: MutationObserver;
  #nextButton: HTMLAnchorElement;
  #prevButton: HTMLAnchorElement;
  #resizeObserver: ResizeObserver;
  #scrollContainer: HTMLElement;
  #slots: Record<string, HTMLSlotElement> = {};

  get loading() {
    return this.getAttribute("loading") ?? "eager";
  }

  get totalItems() {
    return this.getAttribute("total-items") ?? "0";
  }

  get dataHeadline() {
    return this.getAttribute("data-headline") ?? "";
  }

  get dataTotalItems() {
    return this.getAttribute("data-total-items") ?? "";
  }

  constructor() {
    super();

    // Setup Shadow DDOM
    this.attachShadow({ mode: "open" });
    this.shadowRoot!.appendChild(template.content.cloneNode(true));

    // Setup HTML elements
    this.#slots.items = this.shadowRoot!.querySelector(
      ".items",
    )! as HTMLSlotElement;
    this.#items = this.#slots.items.assignedElements()! as HTMLElement[];
    this.#slots.counter = this.shadowRoot!.querySelector(
      `slot[name="counter"]`,
    )! as HTMLSlotElement;
    this.#itemCounter =
      this.#slots.counter.assignedElements()[0] as HTMLElement;
    this.#currentItem = this.#itemCounter.querySelector(".counter__current")!;
    this.#slots.next = this.shadowRoot!.querySelector(
      `slot[name="next"]`,
    )! as HTMLSlotElement;
    this.#nextButton =
      this.#slots.next.assignedElements()[0]! as HTMLAnchorElement;
    this.#slots.previous = this.shadowRoot!.querySelector(
      `slot[name="previous"]`,
    )! as HTMLSlotElement;
    this.#prevButton =
      this.#slots.previous.assignedElements()[0]! as HTMLAnchorElement;
    this.#scrollContainer =
      this.shadowRoot!.querySelector(".scroll-container")!;
    this.#slots.dots = this.shadowRoot!.querySelector(
      `slot[name="dots"]`,
    )! as HTMLSlotElement;
    this.#dots = this.#slots.dots.assignedElements()[0]! as DotIndicator;

    // Setup IntersectionObserver
    this.#itemsObserver = new IntersectionObserver(this.#handleIntersection, {
      root: this.#scrollContainer,
      rootMargin: "0px",
      threshold: 0.8,
    });

    // Setup InteractionObserver
    if (this.loading === "interaction") {
      this.#interactionObserver = new InteractionObserver(
        this.#handleInteraction,
      );
    }

    // Setup MutationObserver
    this.#mutationObserver = new MutationObserver(this.#handleMutation);

    //Setup ResizeObserver
    this.#resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Do not use scrollBy, it has issues with zoom on Safari 18.0.1
        this.#scrollContainer.scroll({
          behavior: "instant", // Prevent scrolling issues in combination with css scroll-snap-type
          left: entry.contentRect.width * this.#currentItemNodeIndex,
        });
      }
    });
  }

  connectedCallback() {
    // Setup IntersectionObserver for each item
    // Button visibility and item counter are updated on the initial intersection callback.
    for (const item of this.#items) {
      this.#itemsObserver.observe(item);
    }

    this.#resizeObserver.observe(this.#scrollContainer);

    // Setup MutationObserver to add dynamically loaded items to the IntersectionObserver
    this.#mutationObserver.observe(this, { childList: true });

    // Setup event listeners
    if (this.#nextButton) {
      this.#nextButton.addEventListener("click", this.#scrollNext);
    }
    if (this.#prevButton) {
      this.#prevButton.addEventListener("click", this.#scrollPrevious);
    }

    this.loading === "interaction"
      ? this.#interactionObserver?.observe(document.body)
      : this.#handleInteraction();
  }

  getViewportElement() {
    return this.#scrollContainer;
  }

  #handleInteraction = () => {
    this.#interactionObserver?.disconnect();

    // Activate all items
    this.#items.forEach((item) => {
      item.removeAttribute("disabled");
    });
  };

  // Triggers also on intersection of first gallery item (not only when navigating through the gallery)
  #handleIntersection = (entries: IntersectionObserverEntry[]) => {
    const galleryHeadline = this.dataHeadline;
    const totalItemsWithAds = this.dataTotalItems;

    for (const entry of entries) {
      if (entry.isIntersecting && entry.target instanceof HTMLElement) {
        //Set #currentItemNodeIndex to pin position in scroll container
        this.#currentItemNodeIndex = Array.prototype.indexOf.call(
          this.#slots.items!.assignedElements(),
          entry.target,
        );

        this.#itemIndex = entry.target.dataset.itemIndex;
        const itemNumber = entry.target.dataset.itemNumber;
        let itemType = entry.target.dataset.itemType;
        // a requirement from the data team
        if (itemType === "image-element") itemType = "image";

        this.#updateItemCounter(itemNumber);

        // Re-enable component
        const itemUrl = entry.target.dataset.itemUrl;
        const totalItems = this.totalItems;

        this.#updateButtonsVisibility();

        if (this.#intersectionCounter > 0) {
          this.#updateItemClasses();
          this.#updateDotIndicator();
          this.#dispatchChangeEvent(
            this.#intersectionCounter,
            galleryHeadline,
            this.#itemIndex,
            itemNumber,
            itemType,
            itemUrl,
            totalItems,
            totalItemsWithAds,
          );
        }
        this.#intersectionCounter++;
      }
    }
  };

  #handleMutation = (mutationList: MutationRecord[]) => {
    for (const mutation of mutationList) {
      if (mutation.type === "childList") {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              this.#itemsObserver.observe(node);
            }
          });
        }
        if (mutation.removedNodes.length > 0) {
          mutation.removedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              this.#itemsObserver.unobserve(node);
            }
          });
        }
        this.#updateButtonsVisibility();
        this.#updateItemClasses();
      }
    }
  };

  #dispatchChangeEvent = (
    count: number,
    galleryHeadline?: string,
    itemIndex?: string,
    itemNumber?: string,
    itemType?: string,
    itemUrl?: string,
    totalItems?: string,
    totalItemsWithAds?: string,
  ) => {
    const event: ChangeEvent = new CustomEvent(GALLERY_CHANGE_EVENT, {
      detail: {
        count,
        galleryHeadline,
        itemIndex,
        itemNumber,
        itemType,
        itemUrl,
        totalItems,
        totalItemsWithAds,
        adTagIds: [
          "skyscraper_1",
          "skyscraper_2",
          "skyscraper_3",
          "skyscraper_4",
          "superbanner_1",
          "wallpaper_1",
        ],
      },
    });

    this.dispatchEvent(event);
  };

  #scrollNext = (event: MouseEvent) => {
    event.preventDefault();
    // Do not use scrollBy, it has issues with zoom on Safari 18.0.1
    this.#scrollContainer.scroll({
      behavior: "instant", // Prevent scrolling issues in combination with css scroll-snap-type
      left:
        this.#scrollContainer.scrollLeft + this.#scrollContainer.clientWidth,
    });
  };

  #scrollPrevious = (event: MouseEvent) => {
    event.preventDefault();
    // Do not use scrollBy, it has issues with zoom on Safari 18.0.1
    this.#scrollContainer.scroll({
      behavior: "instant", // Prevent scrolling issues in combination with css scroll-snap-type
      left:
        this.#scrollContainer.scrollLeft - this.#scrollContainer.clientWidth,
    });
  };

  #updateButtonsVisibility() {
    this.#nextButton!.inert =
      Math.ceil(
        this.#scrollContainer.scrollLeft + this.#scrollContainer.clientWidth,
      ) >= this.#scrollContainer.scrollWidth; // Prevent rounding issues in zoomed browsers
    this.#prevButton!.inert = this.#scrollContainer.scrollLeft === 0;
  }

  #updateItemCounter(itemNumber?: string) {
    if (itemNumber) {
      this.#currentItem.textContent = itemNumber;
      this.#itemCounter.hidden = false;
    } else {
      this.#itemCounter.hidden = true;
    }
  }

  #updateDotIndicator() {
    if (!this.#itemIndex) return;
    this.#dots.activeDot = parseInt(this.#itemIndex);
  }

  #updateItemClasses() {
    if (!this.#itemIndex) return;
    const current = parseInt(this.#itemIndex);
    const items = this.#slots.items!.assignedElements()! as HTMLElement[];
    items.forEach((item, index) => {
      index++; // Should start at 1, not at 0
      if (index < current) {
        item.classList.add("previous");
      } else {
        item.classList.remove("previous");
      }
      if (index === current) {
        item.classList.add("active");
      } else {
        item.classList.remove("active");
      }
      if (index === current + 1) {
        item.classList.add("next");
      } else {
        item.classList.remove("next");
      }
      if (index > current + 1) {
        item.classList.add("following");
      } else {
        item.classList.remove("following");
      }
    });
  }

  disconnectedCallback() {
    if (this.#nextButton) {
      this.#nextButton.removeEventListener("click", this.#scrollNext);
    }
    if (this.#prevButton) {
      this.#prevButton.removeEventListener("click", this.#scrollPrevious);
    }
    this.#itemsObserver.disconnect();
    this.#interactionObserver?.disconnect();
    this.#mutationObserver.disconnect();
  }
}

"customElements" in window &&
  customElements.get("ws-gallery-embed") === undefined &&
  customElements.define("ws-gallery-embed", GalleryEmbed);

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLElementTagNameMap {
    "ws-gallery-embed": GalleryEmbed;
  }
}
